import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { Prayer } from '../types/prayer';

interface CountdownTimerProps {
  nextPrayer: Prayer | null;
  isNextDayFajr: boolean;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({ nextPrayer, isNextDayFajr }) => {
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calculateTimeLeft = () => {
      if (!nextPrayer) return { hours: 0, minutes: 0, seconds: 0 };

      const now = dayjs();
      const [hours, minutes] = nextPrayer.time.split(':');
      let prayerTime = dayjs()
        .hour(Number(hours))
        .minute(Number(minutes))
        .second(0);

      if (isNextDayFajr) {
        prayerTime = prayerTime.add(1, 'day');
      } else if (prayerTime.isBefore(now)) {
        prayerTime = prayerTime.add(1, 'day');
      }

      const diff = prayerTime.diff(now, 'second');
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;

      return { hours: h, minutes: m, seconds: s };
    };

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    setTimeLeft(calculateTimeLeft());

    return () => clearInterval(timer);
  }, [nextPrayer, isNextDayFajr]);

  if (!nextPrayer) return null;

  return (
    <div className="glass-card p-5 animate-fade-in-up">
      {/* Prayer name header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <span className="text-xl">🕌</span>
          </div>
          <div>
            <p className="text-emerald-400 text-xs font-medium">الصلاة القادمة</p>
            <h2 className="text-xl font-bold text-white">{nextPrayer.name.ar}</h2>
          </div>
        </div>
        <div className="text-left">
          <p className="text-2xl font-bold text-white">{nextPrayer.time}</p>
          {isNextDayFajr && (
            <span className="text-amber-400 text-xs font-medium">غداً</span>
          )}
        </div>
      </div>

      {/* Countdown display */}
      <div className="flex items-center justify-center gap-3" dir="ltr">
        <TimeBlock value={timeLeft.hours} label="ساعة" />
        <span className="text-white/30 text-2xl font-bold">:</span>
        <TimeBlock value={timeLeft.minutes} label="دقيقة" />
        <span className="text-white/30 text-2xl font-bold">:</span>
        <TimeBlock value={timeLeft.seconds} label="ثانية" />
      </div>
    </div>
  );
};

// Separate component for each time block
const TimeBlock: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-sm border border-white/10">
      <span className="text-2xl font-bold text-white tabular-nums">
        {value.toString().padStart(2, '0')}
      </span>
    </div>
    <span className="text-white/50 text-xs mt-2 font-medium">{label}</span>
  </div>
);

export default CountdownTimer;
